<script lang="ts">
	// The form an owner sees between signing in and having a dashboard.
	//
	// Four questions and no more. Everything else a restaurant needs -- the
	// currency, the time zone, the idle timeout, how long a shift lasts, the
	// quick cash buttons -- is set by the server, and the form says so, because
	// an owner setting up a till has no opinion about any of it yet.
	//
	// The first shop's name defaults to the word the placeholder shows, in the
	// language the owner is reading, so an Arabic restaurant does not start
	// with a shop called "Main".

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Field from '../components/Field.svelte';
	import Segmented from '../components/Segmented.svelte';
	import { api } from '../api/client';
	import { message, type Language } from '../i18n/messages';
	import { restaurantCreated, type Account } from '../owner/session.svelte';

	interface Props {
		language: Language;
		onCreated: (account: Account) => void;
	}

	let { language, onCreated }: Props = $props();

	// Seeded for the market the plan names.
	let name = $state('');
	let countryCode = $state('20');
	let shopName = $state('');
	// Here as well as in Settings, because it decides what every screen after
	// this one is written in.
	let chosenLanguage = $state<Language>('ar');

	let working = $state(false);
	let failure = $state('');
	let exists = $state(false);

	const ready = $derived(name.trim().length > 0 && countryCode.length > 0);

	async function create() {
		if (!ready || working) return;
		working = true;
		failure = '';
		exists = false;

		try {
			const { data, error } = await api.POST('/api/owner/accounts', {
				body: {
					name: name.trim(),
					defaultCountryCode: countryCode.trim(),
					language: chosenLanguage,
					shopName: shopName.trim() || message(chosenLanguage, 'createShopDefault'),
				},
			});
			if (error !== undefined || !data) {
				// The server's message is written for the person reading it.
				if (error?.code === 'ACCOUNT_EXISTS') exists = true;
				else failure = error?.message ?? message(language, 'loadFailed');
				return;
			}
			restaurantCreated(data);
			onCreated(data);
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			working = false;
		}
	}
</script>

<section class="screen" data-testid="screen-create-restaurant">
	<div class="card">
		<div class="words">
			<h1>{message(language, 'createTitle')}</h1>
			<p class="instruction">{message(language, 'createInstruction')}</p>
		</div>

		{#if exists}
			<Band tone="flag" testId="create-exists">{message(language, 'createExists')}</Band>
		{/if}
		{#if failure}
			<Band tone="flag" testId="create-error">{failure}</Band>
		{/if}

		<Field
			testId="create-name"
			label={message(language, 'createName')}
			placeholder={message(language, 'createNamePlaceholder')}
			value={name}
			onInput={(v) => (name = v)}
			maxLength={120}
		/>
		<Field
			testId="create-country-code"
			label={message(language, 'createCountryCode')}
			note={message(language, 'createCountryCodeHint')}
			value={countryCode}
			onInput={(v) => (countryCode = v.replace(/\D/g, '').slice(0, 3))}
			inputMode="numeric"
			mono
		/>
		<div class="group">
			<span class="label">{message(language, 'settingsRestaurantLanguage')}</span>
			<Segmented
				label={message(language, 'settingsRestaurantLanguage')}
				options={[
					{
						value: 'ar',
						label: message(language, 'settingsLanguageArabic'),
						testId: 'create-language-ar',
					},
					{
						value: 'msa',
						label: message(language, 'settingsLanguageMSA'),
						testId: 'create-language-msa',
					},
					{
						value: 'en',
						label: message(language, 'settingsLanguageEnglish'),
						testId: 'create-language-en',
					},
				]}
				value={chosenLanguage}
				onChange={(picked) => (chosenLanguage = picked as Language)}
			/>
			<span class="label">{message(language, 'createLanguageNote')}</span>
		</div>
		<Field
			testId="create-shop-name"
			label={message(language, 'createShopName')}
			note={message(language, 'createShopNameHint')}
			placeholder={message(chosenLanguage, 'createShopDefault')}
			value={shopName}
			onInput={(v) => (shopName = v)}
			maxLength={120}
		/>
		<p class="set">{message(language, 'createSetForYou')}</p>

		<Button
			testId="create-submit"
			label={message(language, 'createSubmit')}
			pendingLabel={message(language, 'createWorking')}
			state={working ? 'pending' : 'idle'}
			disabled={!ready}
			wide
			onPress={() => void create()}
		/>
	</div>
</section>

<style>
	.screen {
		min-block-size: 100dvh;
		display: grid;
		place-items: center;
		padding: var(--space-9);
		background: var(--surface-page);
	}

	.card {
		inline-size: 100%;
		max-inline-size: 480px;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-panel);
		padding: var(--card-pad);
	}

	.words {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
	}

	.instruction {
		margin: 0;
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.group {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.set {
		margin: 0;
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}
</style>
